import { useTranslation } from "react-i18next";
import { Panel, PanelHeaderTemplateOptions } from "primereact/panel";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import NadaConsta from "../../../components/nadaConsta";
import { formatarParaMoedaBRL, formatarCPF, formatarCNPJ } from "../../../utils";

function toArray<T = any>(valor: T | T[] | undefined | null): T[] {
  if (!valor) return [];
  return Array.isArray(valor) ? valor : [valor];
}

function formatarDataYYYYMMDD(data?: string | null): string {
  if (!data) return "";
  if (data.includes("/")) return data;
  if (data.length === 8) {
    return `${data.substring(6, 8)}/${data.substring(4, 6)}/${data.substring(0, 4)}`;
  }
  return data;
}

function formatarValorCentavos(valor?: string | null, valorFormatado?: string | null): string {
  if (valorFormatado) return `R$ ${valorFormatado}`;
  if (!valor) return "";
  const numero = Number(valor) / 100;
  return isNaN(numero) ? "" : formatarParaMoedaBRL(numero);
}

function humanizarChave(chave: string): string {
  return chave.replace(/_/g, " ").replace(/\b\w/g, (letra) => letra.toUpperCase());
}

function TabelaDinamica({ itens }: { itens: any[] }) {
  if (!itens || itens.length === 0) return null;
  const chaves = Array.from(
    itens.reduce((acc: Set<string>, item) => {
      Object.keys(item ?? {}).forEach((chave) => acc.add(chave));
      return acc;
    }, new Set<string>())
  );
  return (
    <DataTable size="small" value={itens} tableStyle={{ minWidth: "50rem" }}>
      {chaves.map((chave) => (
        <Column key={chave} field={chave} header={humanizarChave(chave)}></Column>
      ))}
    </DataTable>
  );
}

export default function SollosScpcProtestoNacional({
  sollosScpcProtestoNacional,
}: {
  sollosScpcProtestoNacional: any;
}) {
  const { t } = useTranslation();

  const resultados = sollosScpcProtestoNacional?.resultados;
  const sintese = resultados?.sintese_cadastral;
  const isPJ = !!(sintese?.razao_social || sintese?.nome_fantasia);

  const ccf = resultados?.ccf;
  const ccfOcorrencias = toArray(ccf?.ocorrencia);
  const debitos = resultados?.debitos;
  const debitosOcorrencias = toArray(debitos?.ocorrencia);
  const consultasAnteriores = toArray(resultados?.consultas_anteriores?.ocorrencia);
  const protestosOcorrencias = toArray(resultados?.protestos?.ocorrencia);
  const informacoesOcorrencias = toArray(resultados?.informacoes_complementares?.ocorrencia);
  const telefonesOcorrencias = toArray(resultados?.telefones_anteriores?.ocorrencia);
  const resumoOcorrencias = resultados?.resumo_ocorrencias;

  const headerTemplate =
    (title: string, hasItems: boolean) =>
      (options: PanelHeaderTemplateOptions) => {
        const className = `${options.className} `;
        return (
          <div className={className}>
            {hasItems ? (
              <span style={{ color: "#C70039", fontSize: "1.5rem" }} className="pi pi-exclamation-triangle">
                <span
                  className="p-panel-title"
                  style={{ marginLeft: "1rem", fontFamily: "var(--font-family)", fontSize: "1rem", color: "black" }}
                >
                  {title}
                </span>
              </span>
            ) : (
              <span
                className="p-panel-title"
                style={{ marginLeft: "1rem", fontFamily: "var(--font-family)", fontSize: "1rem", color: "black" }}
              >
                {title}
              </span>
            )}
          </div>
        );
      };

  if (!resultados) {
    return (
      <Panel header={"SCPC Net + Protesto Nacional"}>
        <NadaConsta />
      </Panel>
    );
  }

  return (
    <>
      <Panel
        header={
          isPJ
            ? "SCPC Net + Protesto Nacional - Pessoa Jurídica"
            : "SCPC Net + Protesto Nacional - Pessoa Física"
        }
      >
        {sintese && (
          <>
            <div className="grid">
              <div className="col p-fluid flex flex-wrap">
                <div>
                  <label className="label-dados">{isPJ ? t("razaoSocial") : t("nome")}</label>
                  <Chip style={{ width: "100%" }} label={(isPJ ? sintese.razao_social : sintese.nome) ?? "Não Informado"} />
                </div>
              </div>
              <div className="col">
                <div>
                  <label className="label-dados">{t("documento")}</label>
                  <Chip
                    style={{ width: "100%" }}
                    label={sintese.documento ? (isPJ ? formatarCNPJ(sintese.documento) : formatarCPF(sintese.documento)) : "Não Informado"}
                  />
                </div>
              </div>
            </div>
            <div className="grid">
              {isPJ ? (
                <>
                  <div className="col p-fluid flex flex-wrap">
                    <div>
                      <label className="label-dados">{t("nomeFantasia")}</label>
                      <Chip style={{ width: "100%" }} label={sintese.nome_fantasia ?? "Não Informado"} />
                    </div>
                  </div>
                  <div className="col">
                    <div>
                      <label className="label-dados">{t("dataFundacao")}</label>
                      <Chip style={{ width: "100%" }} label={formatarDataYYYYMMDD(sintese.data_fundacao) || "Não Informado"} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col p-fluid flex flex-wrap">
                    <div>
                      <label className="label-dados">{t("nomeMae")}</label>
                      <Chip style={{ width: "100%" }} label={sintese.mae ?? "Não Informado"} />
                    </div>
                  </div>
                  <div className="col">
                    <div>
                      <label className="label-dados">{t("dt_nascimento")}</label>
                      <Chip style={{ width: "100%" }} label={formatarDataYYYYMMDD(sintese.data_nascimento) || "Não Informado"} />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="grid">
              <div className="col p-fluid flex flex-wrap">
                <div>
                  <label className="label-dados">{t("situacao")}</label>
                  <Chip style={{ width: "100%" }} label={sintese.condicao ?? "Não Informado"} />
                </div>
              </div>
              {!isPJ && (
                <div className="col">
                  <div>
                    <label className="label-dados">{t("tituloEleitor")}</label>
                    <Chip
                      style={{ width: "100%" }}
                      label={sintese.titulo_eleitor && sintese.titulo_eleitor.length ? String(sintese.titulo_eleitor) : "Não Informado"}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {resumoOcorrencias && (
          <div className="flex-consulta flex-wrap gap-3" style={{ marginTop: "1rem" }}>
            <Card className="flex-1" style={{ backgroundColor: "#2e2eb8" }}>
              <div className="flex-consulta justify-content-between gap-5">
                <div className="flex-consulta flex-column gap-1">
                  <span style={{ color: "white" }} className="text-sm">{t("ccf")}</span>
                  <span style={{ color: "white" }} className="font-bold text-lg">{resumoOcorrencias.ccf ?? 0}</span>
                </div>
                <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: "white", color: "#183462" }}>
                  <i className="pi pi-file" />
                </span>
              </div>
            </Card>
            <Card className="flex-1" style={{ backgroundColor: "#2e2eb8" }}>
              <div className="flex-consulta justify-content-between gap-5">
                <div className="flex-consulta flex-column gap-1">
                  <span style={{ color: "white" }} className="text-sm">{t("pendencias")}</span>
                  <span style={{ color: "white" }} className="font-bold text-lg">{resumoOcorrencias.debitos ?? 0}</span>
                </div>
                <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: "white", color: "#183462" }}>
                  <i className="pi pi-dollar" />
                </span>
              </div>
            </Card>
            <Card className="flex-1" style={{ backgroundColor: "#2e2eb8" }}>
              <div className="flex-consulta justify-content-between gap-5">
                <div className="flex-consulta flex-column gap-1">
                  <span style={{ color: "white" }} className="text-sm">{t("protestos")}</span>
                  <span style={{ color: "white" }} className="font-bold text-lg">{resumoOcorrencias.protestos ?? 0}</span>
                </div>
                <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: "white", color: "#183462" }}>
                  <i className="pi pi-exclamation-triangle" />
                </span>
              </div>
            </Card>
            <Card className="flex-1" style={{ backgroundColor: "#2e2eb8" }}>
              <div className="flex-consulta justify-content-between gap-5">
                <div className="flex-consulta flex-column gap-1">
                  <span style={{ color: "white" }} className="text-sm">{t("consultas")}</span>
                  <span style={{ color: "white" }} className="font-bold text-lg">{resumoOcorrencias.consultas_anteriores ?? 0}</span>
                </div>
                <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: "white", color: "#183462" }}>
                  <i className="pi pi-search" />
                </span>
              </div>
            </Card>
          </div>
        )}
      </Panel>
      <br />

      <Panel headerTemplate={headerTemplate(t("ccf"), ccfOcorrencias.length > 0)}>
        {ccfOcorrencias.length === 0 ? (
          <NadaConsta />
        ) : (
          <>
            {ccf?.quantidade_total_cheques && (
              <p>
                <strong>{t("quantidadeCheques")}:</strong> {ccf.quantidade_total_cheques}
              </p>
            )}
            <DataTable size="small" value={ccfOcorrencias} tableStyle={{ minWidth: "50rem" }}>
              <Column field="banco" header={t("banco")}></Column>
              <Column field="agencia" header={t("agencia")}></Column>
              <Column field="motivo" header={t("motivo")}></Column>
              <Column field="quantidade" header={t("quantidade")}></Column>
              <Column field="origem" header={t("origem")}></Column>
              <Column field="data_remessa" header={t("dataRegistro")}></Column>
              <Column field="ultimo_cheque" header={t("dataUltimaOcorrencia")}></Column>
            </DataTable>
          </>
        )}
      </Panel>
      <br />

      <Panel headerTemplate={headerTemplate(t("pendencias"), debitosOcorrencias.length > 0)}>
        {debitosOcorrencias.length === 0 ? (
          <NadaConsta />
        ) : (
          <DataTable size="small" value={debitosOcorrencias} tableStyle={{ minWidth: "50rem" }}>
            <Column field="uf" header={t("uf")}></Column>
            <Column field="cidade" header={t("cidade")}></Column>
            <Column field="tipo" header={t("tipo")}></Column>
            <Column field="condicao" header={t("situacao")}></Column>
            <Column field="contrato" header={t("contrato")}></Column>
            <Column field="informante" header={t("informante")}></Column>
            <Column header={t("dataOcorrencia")} body={(rowData) => formatarDataYYYYMMDD(rowData.data_ocorrencia)}></Column>
            <Column header={t("dataRegistro")} body={(rowData) => formatarDataYYYYMMDD(rowData.data_disponilizacao)}></Column>
            <Column header={t("valor")} body={(rowData) => formatarValorCentavos(rowData.valor, rowData.valorn)}></Column>
          </DataTable>
        )}
      </Panel>
      <br />

      <Panel headerTemplate={headerTemplate(t("protestos"), protestosOcorrencias.length > 0)}>
        {protestosOcorrencias.length === 0 ? <NadaConsta /> : <TabelaDinamica itens={protestosOcorrencias} />}
      </Panel>
      <br />

      <Panel header={t("consultas")}>
        {consultasAnteriores.length === 0 ? (
          <NadaConsta />
        ) : (
          <DataTable size="small" value={consultasAnteriores} tableStyle={{ minWidth: "50rem" }}>
            <Column header={t("data")} body={(rowData) => formatarDataYYYYMMDD(rowData.data)}></Column>
            <Column field="tipo" header={t("tipo")}></Column>
            <Column field="descricao" header={t("descricao")}></Column>
          </DataTable>
        )}
      </Panel>

      {informacoesOcorrencias.length > 0 && (
        <>
          <br />
          <Panel header={t("informacoesComplementares")}>
            <TabelaDinamica itens={informacoesOcorrencias} />
          </Panel>
        </>
      )}

      {telefonesOcorrencias.length > 0 && (
        <>
          <br />
          <Panel header={t("telefonesAnteriores")}>
            <TabelaDinamica itens={telefonesOcorrencias} />
          </Panel>
        </>
      )}
    </>
  );
}
